/* eslint-disable camelcase */
import React, { useEffect, useMemo, useState } from 'react';

// Sagas
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import 'react-image-lightbox/style.css';
import { withTranslation } from 'react-i18next';

import classnames from 'classnames';
import toastr from 'toastr';
import 'toastr/build/toastr.min.css';

import {
  Nav, NavItem, NavLink,
} from 'reactstrap';
import ReactApexChart from 'react-apexcharts';

import { get } from '../../helpers/api_helper';
import { buildComparisonLineOptions } from './chartTheme';

const DEFAULT_TAB_ID = 'overall';
const BUSINESS_HOUR_START = 8;
const BUSINESS_HOUR_END = 17;
const ABOVE_AVERAGE_COLOR = '#1f9d68';
const BELOW_AVERAGE_COLOR = '#c65d73';
const CURRENT_LINE_COLOR = '#3559c7';
const AVERAGE_COLOR = 'rgba(148, 163, 184, 0.75)';

function buildComparisonStateSeries(currentSeries = [], averageSeries = []) {
  return currentSeries.map((value, index) => {
    const isDefined = value !== null && value !== undefined;
    const currentValue = isDefined ? Number(value) : null;
    const averageValue = Number(averageSeries[index] ?? 0);

    return {
      isDefined,
      isAboveOrEqual: currentValue >= averageValue,
      currentValue,
    };
  });
}

function buildAverageComparison(currentTotal, averageTotal) {
  const current = Number(currentTotal ?? 0);
  const average = Number(averageTotal ?? 0);

  if (!Number.isFinite(current) || !Number.isFinite(average) || average <= 0) {
    return null;
  }

  const percentage = ((current - average) / average) * 100;
  let direction = 'even';

  if (percentage > 0) {
    direction = 'up';
  } else if (percentage < 0) {
    direction = 'down';
  }

  return {
    direction,
    percentage: Math.abs(percentage),
  };
}

function OrderCountTimeChart(props) {
  const { t, date } = props;

  toastr.options = {
    positionClass: 'toast-top-right',
    newestOnTop: true,
  };

  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState(DEFAULT_TAB_ID);

  const visibleHourRange = useMemo(() => {
    if (data === null) {
      return {
        start: BUSINESS_HOUR_START,
        end: BUSINESS_HOUR_END,
      };
    }

    if (!data.is_today) {
      return {
        start: BUSINESS_HOUR_START,
        end: BUSINESS_HOUR_END,
      };
    }

    const currentHour = new Date().getHours();

    return {
      start: BUSINESS_HOUR_START,
      end: Math.min(currentHour, BUSINESS_HOUR_END),
    };
  }, [data]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await get(`/api/sales/reports/orders/hourly?isoDay=${date}`);
        setData(response);
      } catch (error) {
        toastr.error(t('Error fetching hourly order count data'));
      }
    };

    fetchData();
  }, [date]);

  useEffect(() => {
    setActiveTab(DEFAULT_TAB_ID);
  }, [date]);

  const tabs = useMemo(() => {
    if (data === null) {
      return [];
    }

    return [
      {
        id: DEFAULT_TAB_ID,
        label: t('Overall'),
        ...data.overall,
      },
      ...data.top_sites.map((site) => ({
        id: site.site,
        label: site.site,
        ...site,
      })),
    ];
  }, [data, t]);

  const activeDataset = tabs.find((tab) => tab.id === activeTab) || tabs[0] || null;
  const averageComparison = useMemo(() => {
    if (activeDataset === null) {
      return null;
    }

    return buildAverageComparison(activeDataset.current_total, activeDataset.average_total);
  }, [activeDataset]);
  const visibleLabels = useMemo(() => {
    if (data === null || visibleHourRange.end < visibleHourRange.start) {
      return [];
    }

    return data.labels.slice(visibleHourRange.start, visibleHourRange.end + 1);
  }, [data, visibleHourRange]);

  const visibleCurrentSeries = useMemo(() => {
    if (activeDataset === null || visibleHourRange.end < visibleHourRange.start) {
      return [];
    }

    return activeDataset.current_series.slice(visibleHourRange.start, visibleHourRange.end + 1);
  }, [activeDataset, visibleHourRange]);

  const visibleAverageSeries = useMemo(() => {
    if (activeDataset === null || visibleHourRange.end < visibleHourRange.start) {
      return [];
    }

    return activeDataset.average_series.slice(visibleHourRange.start, visibleHourRange.end + 1);
  }, [activeDataset, visibleHourRange]);

  const hasInProgressHour = useMemo(() => (
    data?.is_today
    && visibleHourRange.end >= BUSINESS_HOUR_START
    && visibleCurrentSeries.length > 0
  ), [data, visibleCurrentSeries.length, visibleHourRange.end]);

  const completedCurrentSeries = useMemo(() => {
    if (!hasInProgressHour) {
      return visibleCurrentSeries;
    }

    if (visibleCurrentSeries.length === 1) {
      return [null];
    }

    return [
      ...visibleCurrentSeries.slice(0, visibleCurrentSeries.length - 1),
      null,
    ];
  }, [hasInProgressHour, visibleCurrentSeries]);

  const inProgressSeries = useMemo(() => {
    if (!hasInProgressHour) {
      return [];
    }

    if (visibleCurrentSeries.length === 1) {
      return [visibleCurrentSeries[0]];
    }

    return [
      ...Array.from({ length: visibleCurrentSeries.length - 2 }, () => null),
      visibleCurrentSeries[visibleCurrentSeries.length - 2],
      visibleCurrentSeries[visibleCurrentSeries.length - 1],
    ];
  }, [hasInProgressHour, visibleCurrentSeries]);

  const comparisonPoints = useMemo(() => (
    buildComparisonStateSeries(visibleCurrentSeries, visibleAverageSeries)
  ), [visibleAverageSeries, visibleCurrentSeries]);

  const inProgressColor = useMemo(() => {
    if (!hasInProgressHour || comparisonPoints.length === 0) {
      return CURRENT_LINE_COLOR;
    }

    const latestPoint = comparisonPoints[comparisonPoints.length - 1];
    return latestPoint.isAboveOrEqual ? ABOVE_AVERAGE_COLOR : BELOW_AVERAGE_COLOR;
  }, [comparisonPoints, hasInProgressHour]);

  const markerDiscrete = useMemo(() => {
    const pointCount = hasInProgressHour
      ? Math.max(visibleCurrentSeries.length - 1, 0)
      : visibleCurrentSeries.length;

    return comparisonPoints
      .slice(0, pointCount)
      .map((point, dataPointIndex) => ({
        seriesIndex: 0,
        dataPointIndex,
        fillColor: point.isAboveOrEqual ? ABOVE_AVERAGE_COLOR : BELOW_AVERAGE_COLOR,
        strokeColor: point.isAboveOrEqual ? ABOVE_AVERAGE_COLOR : BELOW_AVERAGE_COLOR,
        size: 5,
      }));
  }, [comparisonPoints, hasInProgressHour, visibleCurrentSeries.length]);

  const chartOptions = useMemo(() => {
    if (data === null || activeDataset === null || visibleLabels.length === 0) {
      return null;
    }

    return buildComparisonLineOptions({
      categories: visibleLabels,
      yTitle: t('Order Count'),
      tooltipFormatter: (value) => `${Number(value).toFixed(1)} orders`,
      tooltipEnabled: false,
      markerDiscrete,
      colors: hasInProgressHour
        ? [CURRENT_LINE_COLOR, inProgressColor, AVERAGE_COLOR]
        : [CURRENT_LINE_COLOR, AVERAGE_COLOR],
      dashArray: hasInProgressHour
        ? [0, 7, 6]
        : [0, 6],
      strokeWidth: hasInProgressHour
        ? [3, 3, 3]
        : [3, 3],
    });
  }, [data, activeDataset, visibleLabels, hasInProgressHour, inProgressColor, markerDiscrete, t]);

  const chartSeries = useMemo(() => {
    if (activeDataset === null || visibleLabels.length === 0) {
      return null;
    }

    const series = [
      {
        name: t('Selected Day'),
        data: hasInProgressHour ? completedCurrentSeries : visibleCurrentSeries,
      },
    ];

    if (hasInProgressHour) {
      series.push({
        name: t('In progress'),
        data: inProgressSeries,
      });
    }

    series.push({
      name: t(`Average (${data?.historical_weeks || 0} weeks)`),
      data: visibleAverageSeries,
    });

    return series;
  }, [
    activeDataset,
    completedCurrentSeries,
    data,
    hasInProgressHour,
    inProgressSeries,
    visibleAverageSeries,
    visibleCurrentSeries,
    visibleLabels.length,
    t,
  ]);

  return (
    <div className="dashboard-chart">
      {tabs.length > 0 && (
        <div className="dashboard-chart-tabs">
          <Nav tabs className="dashboard-chart-tabs__nav">
            {tabs.map((tab) => (
              <NavItem key={tab.id}>
                <NavLink
                  href="#"
                  className={classnames('dashboard-chart-tabs__link', {
                    active: activeTab === tab.id,
                  })}
                  onClick={(event) => {
                    event.preventDefault();
                    setActiveTab(tab.id);
                  }}
                >
                  {tab.label}
                </NavLink>
              </NavItem>
            ))}
          </Nav>
          <div className="dashboard-chart-tabs__summary">
            <div className={classnames(
              'dashboard-chart-tabs__summary-pill',
              averageComparison && `is-${averageComparison.direction}`,
            )}
            >
              <span className="dashboard-chart-tabs__summary-label">
                {data?.is_today
                  ? t('Day so far vs 8-week average')
                  : t('Full day vs 8-week average')}
              </span>
              <span className="dashboard-chart-tabs__summary-value">
                {averageComparison === null ? (
                  t('Not available')
                ) : (
                  <>
                    {averageComparison.direction !== 'even' && (
                      <span
                        className={`mdi mdi-arrow-${averageComparison.direction}-bold`}
                        aria-hidden="true"
                      />
                    )}
                    {`${averageComparison.percentage.toFixed(1)}% ${t(averageComparison.direction)}`}
                  </>
                )}
              </span>
            </div>
          </div>
        </div>
      )}
      {chartOptions && chartSeries && (
        <ReactApexChart options={chartOptions} series={chartSeries} type="line" height={360} />
      )}
      {chartOptions === null && visibleLabels.length > 0 && (
        <div className="text-center">
          <span className="mdi mdi-loading mdi-spin fs-2" />
        </div>
      )}
      {data !== null && visibleLabels.length === 0 && (
        <div className="dashboard-chart__empty text-center text-muted pt-4">
          {t('No business hours have happened yet for the selected day.')}
        </div>
      )}
    </div>
  );
}

OrderCountTimeChart.propTypes = {
  // eslint-disable-next-line react/require-default-props
  t: PropTypes.any,
  date: PropTypes.string.isRequired,
};

export default connect(() => ({

}), {})(withTranslation()(OrderCountTimeChart));
